import {
  AccordionGroup,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useConstant,
  useMixpanel,
  usePersistedTabs,
  useTabs,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useCurrentAppLayoutId } from '../../../hooks/useAppLayoutId';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import type { TradeTabProps } from '../../../types/LayoutConfig';
import { DEFAULT_TRADE_TABS, NEW_TRADE_TAB_DEFAULTS, TRADE_TABS_EXCLUDED_IDS } from './defaults';
import { FilteredTrades } from './FilteredTrades';
import { TRADES_BLOTTER_PREFIX } from './tokens';
import { TradesFilterRow } from './TradesFilterRow';

const tabLabeler = tabLabelerEnumerated('Trades');
function getBlotterID(tabID: string | undefined) {
  return `${TRADES_BLOTTER_PREFIX}/${tabID}`;
}

export function Trades() {
  const layoutId = useCurrentAppLayoutId();
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  }, [mixpanel]);

  const defaultInitialItems = useConstant(DEFAULT_TRADE_TABS);
  const [excludedIDs] = useState(TRADE_TABS_EXCLUDED_IDS);

  const persistedTabs = usePersistedTabs<TradeTabProps>(TRADES_BLOTTER_PREFIX, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    excludedIDs,
    onSelect: handleSelect,
  });

  const tabs = useTabs<TradeTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  return (
    <Tabs {...tabs} style={{ height: '100%' }} size={TabSize.Large}>
      <TabList isBordered rightItems={<TradesFilterRow />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => (
          <AccordionGroup key={tab.id}>
            <FilteredTrades
              key={idx}
              blotterPersistID={`${TRADES_BLOTTER_PREFIX}/${tab.id}`}
              tabLabel={tab.label}
              showAllDecimals={tab.showAllDecimals}
              setShowAllDecimals={showAllDecimals => tabs.updateItem({ ...tab, showAllDecimals })}
              defaultColumns={tab.defaultColumns ?? NEW_TRADE_TAB_DEFAULTS.defaultColumns}
              defaultFilter={tab.defaultFilter ?? NEW_TRADE_TAB_DEFAULTS.defaultFilter}
              onCloneTab={handleCloneTab}
              initialIsOpen={initialFiltersOpen}
              showAPIOrdersToggle={true}
              layoutId={layoutId}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
